import { Field, Form, Formik } from "formik";
import { MouseEventHandler } from "react";

import { Case, Location } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { locationAdminSchema } from "shared/utils/caseSchema";
import { locationOptions } from "shared/utils/usRegions";
import {
  canValidate,
  getErrors,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import { shallowLocationComparison } from "./locationFormUtils";
import FormErrors from "../../editCasePanel/FormErrors";

type LocationFormProps = {
  accessionCompleted?: string | null;
  adminCase: CaseAdminDetail;
  billingType?: Case.billing_type | null;
  location: Location;
  onClose: MouseEventHandler<HTMLButtonElement>;
  onSubmit: Function;
  disableSubmit?: boolean;
  thirdPartyRequireLocationFields?: boolean;
};

const LocationForm = ({
  accessionCompleted,
  adminCase,
  billingType,
  location,
  onClose,
  onSubmit,
  disableSubmit = false,
  thirdPartyRequireLocationFields = false,
}: LocationFormProps) => {
  const handleOnSubmit = (values: Partial<Location>) => {
    if (disableSubmit || !location) return;

    onSubmit({
      ...shallowLocationComparison(location, values),
      id: location.id,
    });
  };
  const requireLocationFields = thirdPartyRequireLocationFields
    ? billingType === Case.billing_type.GLOBAL
    : false;
  const canValidateForm = canValidate(
    adminCase?.patient?.clinic?.customer_type
  );

  return (
    <Formik
      initialValues={{
        ...location,
      }}
      enableReinitialize
      onSubmit={handleOnSubmit}
      validateOnBlur={false}
      validationSchema={canValidateForm ? locationAdminSchema : null}
      validateOnMount={true}
    >
      {({ resetForm, dirty, errors, touched }) => {
        const allErrors = getErrors(errors, touched, !accessionCompleted);

        return (
          <Form className="AdminPanelForm">
            <div className="panelContainer">
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Contact Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="phone1">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone"
                        id="phone1"
                        {...field}
                        required={requireLocationFields}
                        disabled={disableSubmit}
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "phone1")}
                      />
                    )}
                  </Field>
                  <Field name="phone2">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone 2"
                        id="phone2"
                        {...field}
                        disabled={disableSubmit}
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "phone2")}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Address
                </legend>
                <Field name="street">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 1"
                      id="street"
                      {...field}
                      required={requireLocationFields}
                      disabled={disableSubmit}
                    />
                  )}
                </Field>
                <Field name="street2">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 2"
                      id="street2"
                      {...field}
                      disabled={disableSubmit}
                    />
                  )}
                </Field>
                <div className="panelContainer__inputGroup">
                  <Field name="city">
                    {({ field }: any) => (
                      <TextInput
                        label="City"
                        id="city"
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="state">
                    {({ field }: any) => (
                      <SelectInput
                        label="State"
                        options={locationOptions}
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="zipcode">
                    {({ field }: any) => (
                      <TextInput
                        label="Zip Code"
                        id="zipcode"
                        {...field}
                        required={requireLocationFields}
                        disabled={disableSubmit}
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "zipcode")}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <FormErrors errors={allErrors} />
            </div>
            <footer>
              <PathologyWatchButton onClick={onClose}>
                Cancel
              </PathologyWatchButton>

              <div className="panelContainer__inputGroup">
                <PathologyWatchButton
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  color="green"
                  disabled={
                    !dirty || disableSubmit || Object.keys(allErrors).length > 0
                  }
                >
                  Save
                </PathologyWatchButton>
              </div>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default LocationForm;
